import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const LMCourseList = React.lazy(() => import("./LMCourseList"));
const LMCourseView = React.lazy(() => import("./LMCourseView"));
const LMCourseEditor = React.lazy(() => import("./LMCourseEditor"));
const LMModuleEditor = React.lazy(() => import("./LMModuleEditor"));
const LMLessonEditor = React.lazy(() => import("./LMLessonEditor"));
const LMLessonView = React.lazy(() => import("./LMLessonView"));
const LMCourseAdmin = React.lazy(() => import("./LMCourseAdmin"));
const LMPublicCourseView = React.lazy(() => import("./LMPublicCourseView"));
const LMSharedLessonView = React.lazy(() => import("./LMSharedLessonView"));
const LMDashboard = React.lazy(() => import("./LMDashboard"));
const LMCertificateView = React.lazy(() => import("./LMCertificateView"));
const LMMyCertificates = React.lazy(() => import("./LMMyCertificates"));
const LMAnalytics = React.lazy(() => import("./LMAnalytics"));
const LMWhatsNew = React.lazy(() => import("./LMWhatsNew"));
const LMSettings = React.lazy(() => import("./LMSettings"));

const lmModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "lm",
    name: "Learning Management",
    description: "Create and manage online courses with video lessons, progress tracking, and certificates",
    version: "0.0.1",
    enabled: true,
    category: "education",
    order: 17,
    icon: "🎓",
    color: "#3B82F6",

    // 📝 VERSION CHANGELOG
    // Track what's new in each release
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production LMS",
            features: [
                "✅ Production-ready learning management system",
                "✅ Complete course creation and delivery platform",
                "✅ Full module configuration integration",
                "✅ Comprehensive permissions system",
                "✅ Student and instructor dashboards",
                "✅ Certificate generation and verification"
            ],
            notes: "Official stable release with complete LMS functionality for production use."
        },
        {
            version: "0.2.0",
            releaseDate: "2025-11-17",
            title: "Modular What's New System",
            features: [
                "✨ Created modular What's New architecture",
                "✨ Integrated changelog display in module",
                "✨ Roadmap visualization",
                "✨ Centralized version tracking",
                "✨ Enhanced module documentation"
            ],
            notes: "Enhanced module system with centralized changelog and roadmap management."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-11-14",
            title: "Module Configuration & Tokenization",
            features: [
                "✅ Created comprehensive moduleConfig.ts (454 lines)",
                "✅ Defined all routes and permissions",
                "✅ Added navigation structure",
                "✅ Configured quick actions",
                "✅ Tokenization system integration",
                "✅ Business Builder system integration"
            ],
            notes: "Formal module configuration and system tokenization implementation."
        },
        {
            version: "0.0.1",
            releaseDate: "2023-11-14",
            title: "Initial Release - Core LMS Features",
            features: [
                "✅ Course creation and management with markdown import",
                "✅ Module and lesson organization with drag-and-drop reordering",
                "✅ Video upload with chunked IPFS storage",
                "✅ Markdown content editor with live preview",
                "✅ Attachment system (PDFs, worksheets, documents)",
                "✅ Student enrollment and progress tracking",
                "✅ Mark lessons complete/incomplete",
                "✅ Progress bar and completion indicators",
                "✅ Resume learning (last accessed lesson)",
                "✅ Complete 7-Day Business Builder Bootcamp course (24 lessons)",
                "✅ Public and private course support",
                "✅ Free preview lessons",
                "🔨 Student dashboard (placeholder)",
                "🔨 Certificate system (placeholder)",
                "🔨 Analytics dashboard (placeholder)"
            ],
            notes: "Initial massive implementation with complete LMS platform (7,000+ lines of code). All core features and routing configured."
        }
    ],

    // 🗺️ ROADMAP - Coming Soon Features
    // Track planned features and their development status
    roadmap: [
        {
            title: "Student Dashboard",
            description: "Personalized dashboard showing enrolled courses, progress, and recommended next steps",
            targetVersion: "0.1.0",
            priority: "high",
            status: "planned",
            features: [
                "View all enrolled courses with progress bars",
                "Continue where you left off (last accessed lesson)",
                "Recently completed lessons history",
                "Course recommendations based on interests",
                "Time spent learning statistics"
            ]
        },
        {
            title: "Certificate System",
            description: "Automated certificate generation and public sharing for completed courses",
            targetVersion: "0.1.0",
            priority: "high",
            status: "planned",
            features: [
                "Auto-generate certificates on course completion",
                "Beautiful certificate design with branding",
                "Public certificate verification pages",
                "Download certificates as PDF",
                "Share certificates on LinkedIn",
                "Certificate verification via unique ID"
            ]
        },
        {
            title: "Analytics & Reporting",
            description: "Comprehensive analytics for instructors and admins to track course performance",
            targetVersion: "0.2.0",
            priority: "medium",
            status: "planned",
            features: [
                "Real-time enrollment tracking",
                "Course completion rates",
                "Student engagement metrics",
                "Popular courses dashboard",
                "Revenue tracking (if applicable)",
                "Export reports as CSV/PDF"
            ]
        },
        {
            title: "Interactive Features",
            description: "Quizzes, assignments, and interactive learning elements",
            targetVersion: "0.3.0",
            priority: "medium",
            status: "planned",
            features: [
                "Multiple choice quizzes",
                "Assignment submissions",
                "Grading and feedback system",
                "Discussion forums per lesson",
                "Q&A for students and instructors"
            ]
        },
        {
            title: "Course Builder Enhancements",
            description: "Advanced tools for course creation and management",
            targetVersion: "0.2.0",
            priority: "low",
            status: "planned",
            features: [
                "Bulk lesson import from multiple markdown files",
                "Course cloning/duplication",
                "Course templates library",
                "Automatic video transcription",
                "Subtitle/caption support",
                "Multi-language course support"
            ]
        },
        {
            title: "SMS & Email Campaign Integration",
            description: "Automated notifications and drip campaigns tied to course progress",
            targetVersion: "0.3.0",
            priority: "low",
            status: "planned",
            features: [
                "Daily lesson reminders via SMS",
                "Email drip campaigns based on enrollment",
                "Progress check-in notifications",
                "Completion congratulations",
                "Re-engagement campaigns for inactive students"
            ]
        },
        {
            title: "Social & Community Features",
            description: "Build community and engagement around courses",
            targetVersion: "0.4.0",
            priority: "low",
            status: "planned",
            features: [
                "Course ratings and reviews",
                "Student testimonials",
                "Discussion forums",
                "Student profiles and achievements",
                "Instructor profiles and bios",
                "Course bundles and learning paths"
            ]
        }
    ],

    // 🛣️ ROUTES — permissionLevel resolved from backend lm/permissions.ts
    routes: [
        {
            path: "/lm/dashboard",
            component: LMDashboard,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/lm",
            component: LMCourseList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/lm/courses/new",
            component: LMCourseEditor,
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/lm/courses/:id/edit",
            component: LMCourseEditor,
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/lm/courses/:courseId/modules/new",
            component: LMModuleEditor,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.md" },
        },
        {
            path: "/lm/courses/:courseId/modules/:id/edit",
            component: LMModuleEditor,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.md" },
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/new",
            component: LMLessonEditor,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/:id/edit",
            component: LMLessonEditor,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/:id",
            component: LMLessonView,
            permissions: [], // Component handles login modal for enrollment
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/lesson/:id/edit",
            component: LMLessonEditor,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/lesson/:id",
            component: LMLessonView,
            permissions: [], // Component handles login modal
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/settings",
            component: LMSettings,
            permissionLevel: "admin",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/lm/admin",
            component: LMCourseAdmin,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/lm/courses/:id",
            component: LMCourseView,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/lm/my-certificates",
            component: LMMyCertificates,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/lm/analytics",
            component: LMAnalytics,
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/lm/whats-new",
            component: LMWhatsNew,
            permissionLevel: "create",
            useLayout: true,
        },
        {
            path: "/lm/certificate/:slug",
            component: LMCertificateView,
            permissions: [], // Public certificate page
            useLayout: true,
            layoutOptions: { maxW: "container.lg", noBreadcrumb: true },
        },
        {
            path: "/lm/shared/:token",
            component: LMSharedLessonView,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.lg", noBreadcrumb: true },
        },
        {
            path: "/lm/course/:slug",
            component: LMPublicCourseView,
            permissions: [], // Public marketing page
            useLayout: true,
            layoutOptions: { maxW: "container.lg", noBreadcrumb: true },
        },
    ],

    // 🧭 NAVIGATION — My Dashboard first (default landing for students)
    navigation: [
        {
            label: "My Dashboard",
            path: "/lm/dashboard",
            icon: "🎯",
            permissionLevel: "view",
        },
        {
            label: "All Courses",
            path: "/lm",
            icon: "📚",
            permissionLevel: "view",
        },
        {
            label: "My Certificates",
            path: "/lm/my-certificates",
            icon: "🏆",
            permissionLevel: "view",
        },
        {
            label: "Create Course",
            path: "/lm/courses/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Analytics",
            path: "/lm/analytics",
            icon: "📊",
            permissionLevel: "create",
        },
        {
            label: "Settings",
            path: "/lm/settings",
            icon: "🎨",
            permissionLevel: "admin",
        },
        {
            label: "Admin Dashboard",
            path: "/lm/admin",
            icon: "⚙️",
            permissionLevel: "admin",
        },
        {
            label: "What's New",
            path: "/lm/whats-new",
            icon: "✨",
            permissionLevel: "create",
        },
    ],

    // 🔐 PERMISSIONS — resolved from backend lm/permissions.ts
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "My Dashboard",
            path: "/lm/dashboard",
            icon: "🎯",
            description: "View your enrolled courses and progress"
        },
        {
            label: "Browse Courses",
            path: "/lm",
            icon: "📚",
            description: "View all available courses"
        },
        {
            label: "My Certificates",
            path: "/lm/my-certificates",
            icon: "🏆",
            description: "View your earned certificates"
        },
        {
            label: "Create Course",
            path: "/lm/courses/new",
            icon: "➕",
            description: "Create a new course with modules and lessons"
        },
        {
            label: "Analytics",
            path: "/lm/analytics",
            icon: "📊",
            description: "View course performance metrics"
        },
        {
            label: "Admin Dashboard",
            path: "/lm/admin",
            icon: "⚙️",
            description: "Manage all courses and enrollments"
        },
        {
            label: "What's New",
            path: "/lm/whats-new",
            icon: "✨",
            description: "View changelog and roadmap"
        }
    ]
};

export default lmModuleConfig;
